import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmt, fmtB } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PageHead } from '../components/layout';
import { useActiveCompanyId } from '../lib/queries/companies';
import { useTaxAdjSummary, useTaxAdjustments } from '../lib/queries/tax-adjustments';

type TabKey = 'dashboard' | 'addback' | 'deduct' | 'fa_dep' | 'manual' | 'pnd50';
type AdjType = 'add' | 'deduct';
type AdjStatus = 'auto' | 'reviewed';
type CatSource = 'fixedasset' | 'lease' | 'empben' | 'ar';
type CatCode =
  | 'CAR_DEP'
  | 'FA_DEP_DIFF'
  | 'TFRS16_ROU'
  | 'TAS19'
  | 'PROV_BAD'
  | 'ENT_OVER'
  | 'PENALTY'
  | 'PERSONAL'
  | 'FX_UNREAL'
  | 'GOODWILL'
  | 'LEASE_PAID'
  | 'SEV_PAID'
  | 'TAX_DEP_HIGH'
  | 'RND_2X'
  | 'DONATE_2X'
  | 'DIV_50';

interface AdjCategory {
  code: CatCode;
  label: string;
  law: string;
  type: AdjType;
  source?: CatSource;
}

interface Adjustment {
  id: string;
  cat: CatCode;
  bookAmt: number;
  taxAmt: number;
  diff: number;
  ref: string;
  auto: boolean;
  status: AdjStatus;
  auditor: boolean;
  auditorNote?: string;
}

interface FaAsset {
  id: string;
  name: string;
  cost: number;
  bookLife: number;
  taxLife: number;
  bookDep: number;
  taxDep: number;
  cap?: string;
  note: string;
}

interface Totals {
  adds: readonly Adjustment[];
  deds: readonly Adjustment[];
  totalAdd: number;
  totalDed: number;
  bookProfit: number;
  taxProfit: number;
  taxRate: number;
  taxAmount: number;
  etr: number;
}

const ADJ_CATS: readonly AdjCategory[] = [
  {
    code: 'CAR_DEP',
    label: 'ค่าเสื่อมรถยนต์ส่วนเกิน 1 ล้าน',
    law: 'พ.ร.ฎ.145 + ม.65ทวิ',
    type: 'add',
    source: 'fixedasset',
  },
  {
    code: 'FA_DEP_DIFF',
    label: 'ค่าเสื่อมราคาบัญชี > ภาษี',
    law: 'พ.ร.ฎ.145 อัตราขั้นต่ำ',
    type: 'add',
    source: 'fixedasset',
  },
  {
    code: 'TFRS16_ROU',
    label: 'ค่าเสื่อม ROU + ดอกเบี้ย TFRS 16',
    law: 'ภาษีไม่รับรู้ ROU',
    type: 'add',
    source: 'lease',
  },
  {
    code: 'TAS19',
    label: 'TAS 19 — SC + IC ที่ตั้งสำรอง',
    law: 'ม.65ตรี (1) — Provision',
    type: 'add',
    source: 'empben',
  },
  {
    code: 'PROV_BAD',
    label: 'ค่าเผื่อหนี้สงสัยจะสูญ',
    law: 'ม.65ทวิ (9)',
    type: 'add',
    source: 'ar',
  },
  {
    code: 'ENT_OVER',
    label: 'ค่ารับรองส่วนเกิน 0.3% ของรายได้',
    law: 'ม.65ตรี (4)',
    type: 'add',
  },
  { code: 'PENALTY', label: 'เบี้ยปรับ / เงินเพิ่มภาษี', law: 'ม.65ตรี (6)', type: 'add' },
  {
    code: 'PERSONAL',
    label: 'ค่าใช้จ่ายส่วนตัว / ไม่เกี่ยวธุรกิจ',
    law: 'ม.65ตรี (3, 13)',
    type: 'add',
  },
  {
    code: 'FX_UNREAL',
    label: 'ผลขาดทุนอัตราแลกเปลี่ยน Unrealized',
    law: 'ม.65ทวิ (5)',
    type: 'add',
  },
  { code: 'GOODWILL', label: 'Goodwill Impairment', law: 'ม.65ตรี', type: 'add' },
  {
    code: 'LEASE_PAID',
    label: 'ค่าเช่าจ่ายจริง (แทน TFRS 16)',
    law: 'ม.65 ตรงตามจ่ายจริง',
    type: 'deduct',
    source: 'lease',
  },
  {
    code: 'SEV_PAID',
    label: 'ค่าชดเชยที่จ่ายจริง (TAS 19)',
    law: 'ม.65 ตรงตามจ่ายจริง',
    type: 'deduct',
    source: 'empben',
  },
  {
    code: 'TAX_DEP_HIGH',
    label: 'ค่าเสื่อมภาษี > บัญชี',
    law: 'พ.ร.ฎ.145',
    type: 'deduct',
    source: 'fixedasset',
  },
  { code: 'RND_2X', label: 'R&D หักเพิ่มได้ 2 เท่า', law: 'ม.65ตรี (1) BOI', type: 'deduct' },
  {
    code: 'DONATE_2X',
    label: 'บริจาคใช้ 2 เท่า (โรงเรียน/สาธารณะ)',
    law: 'ม.65ตรี',
    type: 'deduct',
  },
  { code: 'DIV_50', label: 'เงินปันผลรับ 50% ยกเว้น', law: 'ม.65ทวิ (10)', type: 'deduct' },
];

const CAT_MAP: ReadonlyMap<CatCode, AdjCategory> = new Map(ADJ_CATS.map((c) => [c.code, c]));

function getCat(code: CatCode): AdjCategory {
  const c = CAT_MAP.get(code);
  if (!c) throw new Error(`Unknown adjustment category: ${code}`);
  return c;
}

// Fixed-asset depreciation comparison — not yet persisted by API.
const FA_ASSETS: readonly FaAsset[] = [];

const FILE_CHECKLIST: readonly string[] = [
  'รายการบวกกลับ/หักออก ตรวจสอบครบทุกรายการ',
  'Auditor ลงนามรับรองรายการ Manual ทั้งหมด',
  'ข้อมูลจาก Module ต่างๆ ตรงกับ GL',
  'แนบงบการเงิน + รายงานผู้สอบบัญชี',
  'แนบ ภ.ง.ด.51 (ครึ่งปี) ที่ยื่นแล้ว',
  'แนบ 50ทวิ ที่ถูกหัก WHT ระหว่างปี',
];

export const Route = createFileRoute('/taxadj')({
  component: TaxAdjPage,
});

function TaxAdjPage() {
  const [tab, setTab] = useState<TabKey>('dashboard');
  const [showAdd, setShowAdd] = useState(false);
  const [showDetail, setShowDetail] = useState<Adjustment | null>(null);
  const [showFile, setShowFile] = useState(false);
  const [taxYear, setTaxYear] = useState<number>(2026);

  // Wire API — when summary returns rows, use the server-side computed
  // totals; otherwise fall back to the prototype mock array so the page
  // still renders during local dev before any TaxAdjustment rows are
  // posted. The DOM stays identical either way.
  const companyId = useActiveCompanyId();
  const { data: apiSummary } = useTaxAdjSummary(companyId, taxYear);
  const { data: apiRows } = useTaxAdjustments(companyId, { taxYear });

  const totals = useMemo<Totals>(() => {
    const sourceRows: readonly Adjustment[] = (apiRows ?? []).map((r): Adjustment => {
      const cat = CAT_MAP.has(r.code as CatCode) ? (r.code as CatCode) : 'PERSONAL';
      const diff = Number(r.adjustmentAmount);
      const status: AdjStatus = r.status === 'APPROVED' ? 'reviewed' : 'auto';
      return {
        id: r.id.slice(0, 8).toUpperCase(),
        cat,
        bookAmt: Number(r.bookAmount),
        taxAmt: Number(r.taxAmount),
        diff,
        ref: r.description,
        auto: !r.postedBy?.includes('@'),
        status,
        auditor: r.status === 'APPROVED',
      };
    });
    const adds = sourceRows.filter((a) => getCat(a.cat).type === 'add');
    const deds = sourceRows.filter((a) => getCat(a.cat).type === 'deduct');
    const totalAdd = apiSummary
      ? Number(apiSummary.totalAddBack)
      : adds.reduce((s, a) => s + a.diff, 0);
    const totalDed = apiSummary
      ? Number(apiSummary.totalDeduct)
      : deds.reduce((s, a) => s + Math.abs(a.diff), 0);
    const bookProfit = apiSummary ? Number(apiSummary.bookNetIncome) : 0;
    const taxProfit = apiSummary
      ? Number(apiSummary.taxableIncome)
      : bookProfit + totalAdd - totalDed;
    const taxRate = apiSummary ? apiSummary.taxRate : 0.2;
    const taxAmount = apiSummary ? Number(apiSummary.taxAmount) : Math.round(taxProfit * taxRate);
    const etr = bookProfit > 0 ? (taxAmount / bookProfit) * 100 : 0;
    return { adds, deds, totalAdd, totalDed, bookProfit, taxProfit, taxRate, taxAmount, etr };
  }, [apiSummary, apiRows]);

  const manualRows = useMemo(
    () => totals.adds.filter((a) => !a.auto).concat(totals.deds.filter((a) => !a.auto)),
    [totals.adds, totals.deds],
  );

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['dashboard', 'Dashboard'],
    ['addback', `รายการบวกกลับ (${totals.adds.length})`],
    ['deduct', `รายการหักออก (${totals.deds.length})`],
    ['fa_dep', 'ค่าเสื่อม บัญชี vs ภาษี'],
    ['manual', `Manual จาก Auditor (${manualRows.length})`],
    ['pnd50', 'ภ.ง.ด.50 Preparation'],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['รายงานและภาษี', 'กระทบยอดภาษีนิติบุคคล']}
        title={`กระทบยอดกำไรบัญชี ↔ กำไรทางภาษี (Tax Adjustment) — ปี ${taxYear}`}
        actions={
          <>
            <select
              className="select"
              value={String(taxYear)}
              onChange={(e) => setTaxYear(parseInt(e.target.value, 10))}
            >
              <option value="2026">ปีภาษี: 2026</option>
              <option value="2025">2025</option>
            </select>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-3): wire export */
              }}
            >
              <Icon name="download" size={14} /> Export
            </button>
            <button className="btn primary" onClick={() => setShowFile(true)}>
              <Icon name="upload" size={14} /> เตรียม ภ.ง.ด.50
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            กำไรสุทธิทางบัญชี
          </div>
          <div className="value tnum">
            {fmtB(totals.bookProfit)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">YTD ก่อนปรับปรุงภาษี</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            บวกกลับสุทธิ
          </div>
          <div className="value tnum" style={{ color: 'var(--negative)' }}>
            +{fmtB(totals.totalAdd)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            {totals.adds.length} รายการ · {totals.adds.filter((a) => a.auto).length} Auto +{' '}
            {totals.adds.filter((a) => !a.auto).length} Manual
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            หักออก
          </div>
          <div className="value tnum" style={{ color: 'var(--positive)' }}>
            −{fmtB(totals.totalDed)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{totals.deds.length} รายการ</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            ภาษีต้องชำระ (20%)
          </div>
          <div className="value tnum">
            {fmtB(totals.taxAmount)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            ETR <b>{totals.etr.toFixed(2)}%</b> · กำไรภาษี {fmtB(totals.taxProfit)}
          </div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '18px 22px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 12px' }}>
          สมการกระทบยอด กำไรทางบัญชี → กำไรทางภาษี
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr auto 1fr auto 1fr auto 1fr',
            gap: 10,
            alignItems: 'center',
            fontSize: 14,
          }}
        >
          <div
            style={{
              textAlign: 'center',
              padding: '12px',
              background: 'white',
              border: '2px solid var(--border)',
              borderRadius: 8,
            }}
          >
            <div className="meta">กำไรสุทธิทางบัญชี</div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginTop: 4,
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(totals.bookProfit)}
            </div>
          </div>
          <div
            style={{
              fontSize: 22,
              color: 'var(--text-faint)',
              fontFamily: 'var(--font-display)',
            }}
          >
            +
          </div>
          <div
            style={{
              textAlign: 'center',
              padding: '12px',
              background: 'white',
              border: '2px solid var(--negative)',
              borderRadius: 8,
            }}
          >
            <div className="meta">บวกกลับ</div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginTop: 4,
                color: 'var(--negative)',
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(totals.totalAdd)}
            </div>
          </div>
          <div
            style={{
              fontSize: 22,
              color: 'var(--text-faint)',
              fontFamily: 'var(--font-display)',
            }}
          >
            −
          </div>
          <div
            style={{
              textAlign: 'center',
              padding: '12px',
              background: 'white',
              border: '2px solid var(--positive)',
              borderRadius: 8,
            }}
          >
            <div className="meta">หักออก</div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginTop: 4,
                color: 'var(--positive)',
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(totals.totalDed)}
            </div>
          </div>
          <div
            style={{
              fontSize: 22,
              color: 'var(--text-faint)',
              fontFamily: 'var(--font-display)',
            }}
          >
            =
          </div>
          <div
            style={{
              textAlign: 'center',
              padding: '12px',
              background: 'var(--brand-tint)',
              border: '2px solid var(--brand)',
              borderRadius: 8,
            }}
          >
            <div className="meta" style={{ color: 'var(--brand)' }}>
              กำไรสุทธิทางภาษี
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginTop: 4,
                color: 'var(--brand)',
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(totals.taxProfit)}
            </div>
          </div>
        </div>
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            background: 'white',
            borderRadius: 6,
            border: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 14,
          }}
        >
          <span>
            <b>ภาษีนิติบุคคล:</b> {fmt(totals.taxProfit)} × 20% ={' '}
          </span>
          <span
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: 'var(--brand)',
              fontFamily: 'var(--font-display)',
            }}
            className="tnum"
          >
            {fmt(totals.taxAmount)} บาท
          </span>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'dashboard' && <TADashboard totals={totals} onDetail={setShowDetail} />}
      {tab === 'addback' && <TAList rows={totals.adds} onDetail={setShowDetail} />}
      {tab === 'deduct' && <TAList rows={totals.deds} onDetail={setShowDetail} />}
      {tab === 'fa_dep' && <FADepCompare />}
      {tab === 'manual' && (
        <ManualList rows={manualRows} onAdd={() => setShowAdd(true)} onDetail={setShowDetail} />
      )}
      {tab === 'pnd50' && <PND50Prep totals={totals} onFile={() => setShowFile(true)} />}

      {showAdd && <AddManualModal onClose={() => setShowAdd(false)} />}
      {showDetail && <DetailModal item={showDetail} onClose={() => setShowDetail(null)} />}
      {showFile && <FileTaxModal totals={totals} onClose={() => setShowFile(false)} />}
    </div>
  );
}

interface DashboardProps {
  totals: Totals;
  onDetail: (a: Adjustment) => void;
}

function TADashboard({ totals, onDetail }: DashboardProps) {
  const top5Adds = [...totals.adds].sort((a, b) => b.diff - a.diff).slice(0, 5);
  const sortedDeds = [...totals.deds].sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
  if (totals.adds.length === 0 && totals.deds.length === 0) {
    return (
      <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
        ยังไม่มีรายการ
      </div>
    );
  }
  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head">
          <h3>Top 5 — รายการบวกกลับใหญ่สุด</h3>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>รายการ</th>
                <th>กฎหมาย</th>
                <th className="num">บวกกลับ</th>
              </tr>
            </thead>
            <tbody>
              {top5Adds.map((a) => {
                const c = getCat(a.cat);
                return (
                  <tr key={a.id} onClick={() => onDetail(a)} style={{ cursor: 'pointer' }}>
                    <td>
                      <b>{c.label}</b>
                      <div className="meta">{a.ref.slice(0, 40)}</div>
                    </td>
                    <td className="muted">{c.law}</td>
                    <td className="num tnum" style={{ color: 'var(--negative)', fontWeight: 600 }}>
                      +{fmt(a.diff)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Top — รายการหักออก</h3>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>รายการ</th>
                <th>กฎหมาย</th>
                <th className="num">หักออก</th>
              </tr>
            </thead>
            <tbody>
              {sortedDeds.map((a) => {
                const c = getCat(a.cat);
                return (
                  <tr key={a.id} onClick={() => onDetail(a)} style={{ cursor: 'pointer' }}>
                    <td>
                      <b>{c.label}</b>
                      <div className="meta">{a.ref.slice(0, 40)}</div>
                    </td>
                    <td className="muted">{c.law}</td>
                    <td className="num tnum" style={{ color: 'var(--positive)', fontWeight: 600 }}>
                      −{fmt(Math.abs(a.diff))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="card-head">
          <h3>การกระทบยอด — แสดงแบบงบ</h3>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <tbody>
              <tr style={{ background: '#fafafa', fontWeight: 700 }}>
                <td>กำไรสุทธิทางบัญชี (Book Income)</td>
                <td className="num tnum">{fmt(totals.bookProfit)}</td>
              </tr>
              <tr>
                <td
                  colSpan={2}
                  style={{
                    background: '#fce9e7',
                    color: 'var(--negative)',
                    fontWeight: 600,
                    padding: '8px 14px',
                  }}
                >
                  บวกกลับ (Add-back)
                </td>
              </tr>
              {totals.adds.map((a) => {
                const c = getCat(a.cat);
                return (
                  <tr key={a.id} onClick={() => onDetail(a)} style={{ cursor: 'pointer' }}>
                    <td className="indent-1">
                      {c.label} <span className="meta">— {a.ref.slice(0, 50)}</span>
                    </td>
                    <td className="num tnum">+{fmt(a.diff)}</td>
                  </tr>
                );
              })}
              <tr className="subtotal">
                <td>รวมรายการบวกกลับ</td>
                <td className="num tnum" style={{ color: 'var(--negative)' }}>
                  +{fmt(totals.totalAdd)}
                </td>
              </tr>
              <tr>
                <td
                  colSpan={2}
                  style={{
                    background: '#e7f4ec',
                    color: 'var(--positive)',
                    fontWeight: 600,
                    padding: '8px 14px',
                  }}
                >
                  หักออก (Deduct)
                </td>
              </tr>
              {totals.deds.map((a) => {
                const c = getCat(a.cat);
                return (
                  <tr key={a.id} onClick={() => onDetail(a)} style={{ cursor: 'pointer' }}>
                    <td className="indent-1">
                      {c.label} <span className="meta">— {a.ref.slice(0, 50)}</span>
                    </td>
                    <td className="num tnum">−{fmt(Math.abs(a.diff))}</td>
                  </tr>
                );
              })}
              <tr className="subtotal">
                <td>รวมรายการหักออก</td>
                <td className="num tnum" style={{ color: 'var(--positive)' }}>
                  −{fmt(totals.totalDed)}
                </td>
              </tr>
              <tr className="total">
                <td>กำไรสุทธิทางภาษี (Taxable Income)</td>
                <td className="num tnum" style={{ color: 'var(--brand)' }}>
                  {fmt(totals.taxProfit)}
                </td>
              </tr>
              <tr>
                <td>× อัตราภาษีนิติบุคคล</td>
                <td className="num">20%</td>
              </tr>
              <tr className="total">
                <td>ภาษีนิติบุคคลที่ต้องชำระ</td>
                <td className="num tnum" style={{ color: 'var(--brand)' }}>
                  {fmt(totals.taxAmount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface TAListProps {
  rows: readonly Adjustment[];
  onDetail: (a: Adjustment) => void;
}

function TAList({ rows, onDetail }: TAListProps) {
  const sumAbs = rows.reduce((s, a) => s + Math.abs(a.diff), 0);
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>{rows.length} รายการ</span>
        <select className="select" defaultValue="all">
          <option value="all">ที่มา: ทั้งหมด</option>
          <option value="auto">Auto จาก Module</option>
          <option value="manual">Manual จาก Auditor</option>
        </select>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>เลขที่</th>
            <th>รายการ</th>
            <th>กฎหมาย</th>
            <th className="num">ทางบัญชี</th>
            <th className="num">ทางภาษี</th>
            <th className="num">ผลต่าง</th>
            <th>ที่มา</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={9} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                ยังไม่มีรายการ
              </td>
            </tr>
          )}
          {rows.map((a) => {
            const c = getCat(a.cat);
            return (
              <tr key={a.id}>
                <td className="code">
                  <b>{a.id}</b>
                </td>
                <td>
                  <b>{c.label}</b>
                  <div className="meta">{a.ref}</div>
                </td>
                <td className="muted code" style={{ fontSize: 13 }}>
                  {c.law}
                </td>
                <td className="num tnum">{fmt(a.bookAmt)}</td>
                <td className="num tnum">{fmt(a.taxAmt)}</td>
                <td
                  className="num tnum"
                  style={{
                    color: a.diff > 0 ? 'var(--negative)' : 'var(--positive)',
                    fontWeight: 700,
                  }}
                >
                  {a.diff > 0 ? '+' : ''}
                  {fmt(a.diff)}
                </td>
                <td>
                  {a.auto ? (
                    <span className="badge brand">
                      <Icon name="link" size={10} /> {c.source ?? 'Auto'}
                    </span>
                  ) : (
                    <span className="badge warning">
                      <span className="bdot" />
                      Auditor
                    </span>
                  )}
                </td>
                <td>
                  {a.status === 'auto' ? (
                    <span className="badge positive">ตรวจอัตโนมัติ</span>
                  ) : (
                    <span className="badge positive">ผ่าน Auditor</span>
                  )}
                </td>
                <td>
                  <button
                    className="btn sm ghost"
                    style={{ height: 26, fontSize: 13 }}
                    onClick={() => onDetail(a)}
                  >
                    ดู
                  </button>
                </td>
              </tr>
            );
          })}
          {rows.length > 0 && (
            <tr className="total">
              <td colSpan={5}>รวม {rows.length} รายการ</td>
              <td className="num tnum">{fmt(sumAbs)}</td>
              <td colSpan={3} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function FADepCompare() {
  const totalBookDep = FA_ASSETS.reduce((s, a) => s + a.bookDep, 0);
  const totalTaxDep = FA_ASSETS.reduce((s, a) => s + a.taxDep, 0);
  const totalCost = FA_ASSETS.reduce((s, a) => s + a.cost, 0);
  const diff = totalBookDep - totalTaxDep;
  if (FA_ASSETS.length === 0) {
    return (
      <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
        ยังไม่มีข้อมูลสำหรับช่วงเวลานี้
      </div>
    );
  }
  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div>
            <h3>ค่าเสื่อมราคา — บัญชี vs ภาษี (พ.ร.ฎ.145)</h3>
            <div className="sub">
              ภาษีกำหนดอัตราขั้นต่ำ · บัญชีตามอายุที่บริษัทประเมิน ·
              ผลต่างต้องปรับปรุงในการคำนวณภาษี
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '18px',
            background: '#fafafa',
            display: 'grid',
            gridTemplateColumns: 'repeat(4,1fr)',
            gap: 14,
          }}
        >
          <div>
            <div className="meta">ค่าเสื่อมทางบัญชี YTD</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                marginTop: 4,
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(totalBookDep)}
            </div>
          </div>
          <div>
            <div className="meta">ค่าเสื่อมทางภาษี YTD</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                marginTop: 4,
                color: 'var(--brand)',
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(totalTaxDep)}
            </div>
          </div>
          <div>
            <div className="meta">ผลต่าง</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                marginTop: 4,
                color: diff > 0 ? 'var(--negative)' : 'var(--positive)',
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {diff > 0 ? '+' : ''}
              {fmt(diff)}
            </div>
          </div>
          <div>
            <div className="meta">สำหรับงบภาษี</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
              {diff > 0 ? 'บวกกลับ (Add-back)' : 'หักออก (Deduct)'}
            </div>
          </div>
        </div>
      </div>

      <div className="tbl-shell">
        <table className="tbl">
          <thead>
            <tr>
              <th>รหัส</th>
              <th>ทรัพย์สิน</th>
              <th className="num">ราคาทุน</th>
              <th className="num">บัญชี (ปี)</th>
              <th className="num">ค่าเสื่อมบัญชี</th>
              <th className="num">ภาษี (ปี)</th>
              <th className="num">ค่าเสื่อมภาษี</th>
              <th className="num">ผลต่าง</th>
              <th>หมายเหตุ</th>
            </tr>
          </thead>
          <tbody>
            {FA_ASSETS.map((a) => {
              const d = a.bookDep - a.taxDep;
              const color =
                a.bookDep > a.taxDep
                  ? 'var(--negative)'
                  : a.bookDep < a.taxDep
                    ? 'var(--positive)'
                    : 'inherit';
              return (
                <tr key={a.id}>
                  <td className="code">
                    <b>{a.id}</b>
                  </td>
                  <td>{a.name}</td>
                  <td className="num tnum">{fmt(a.cost)}</td>
                  <td className="num">{a.bookLife}</td>
                  <td className="num tnum">{fmt(a.bookDep)}</td>
                  <td className="num">{a.taxLife}</td>
                  <td className="num tnum">{fmt(a.taxDep)}</td>
                  <td className="num tnum" style={{ color, fontWeight: 600 }}>
                    {a.bookDep === a.taxDep ? '0' : (a.bookDep > a.taxDep ? '+' : '') + fmt(d)}
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {a.cap && (
                      <span className="badge warning" style={{ fontSize: 13, marginRight: 6 }}>
                        Cap {a.cap}
                      </span>
                    )}
                    {a.note}
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td colSpan={2}>รวม (Sample)</td>
              <td className="num tnum">{fmt(totalCost)}</td>
              <td />
              <td className="num tnum">{fmt(totalBookDep)}</td>
              <td />
              <td className="num tnum">{fmt(totalTaxDep)}</td>
              <td
                className="num tnum"
                style={{
                  color: diff > 0 ? 'var(--negative)' : 'var(--positive)',
                  fontWeight: 700,
                }}
              >
                {diff > 0 ? '+' : ''}
                {fmt(diff)}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

interface ManualListProps {
  rows: readonly Adjustment[];
  onAdd: () => void;
  onDetail: (a: Adjustment) => void;
}

function ManualList({ rows, onAdd, onDetail }: ManualListProps) {
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          รายการ Manual จาก Auditor — {rows.length} รายการ
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn sm primary" onClick={onAdd}>
            <Icon name="plus" size={12} /> เพิ่มรายการ
          </button>
        </div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>เลขที่</th>
            <th>หมวด</th>
            <th>รายละเอียด</th>
            <th className="num">ผลต่าง</th>
            <th>Auditor</th>
            <th>หมายเหตุ</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                ยังไม่มีรายการ
              </td>
            </tr>
          )}
          {rows.map((a) => {
            const c = getCat(a.cat);
            const note = a.auditorNote;
            const firmTag = note ? (note.split(':')[0] ?? '—') : '—';
            return (
              <tr key={a.id}>
                <td className="code">
                  <b>{a.id}</b>
                </td>
                <td>
                  {c.label}
                  <div className="meta">{c.law}</div>
                </td>
                <td>{a.ref}</td>
                <td
                  className="num tnum"
                  style={{
                    color: a.diff > 0 ? 'var(--negative)' : 'var(--positive)',
                    fontWeight: 600,
                  }}
                >
                  {a.diff > 0 ? '+' : ''}
                  {fmt(a.diff)}
                </td>
                <td className="muted">{firmTag}</td>
                <td className="muted">{note ?? '—'}</td>
                <td>
                  <span className="badge positive">ผ่าน Auditor</span>
                </td>
                <td>
                  <button
                    className="btn sm ghost"
                    style={{ height: 26, fontSize: 13 }}
                    onClick={() => onDetail(a)}
                  >
                    ดู / แก้
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface PND50Props {
  totals: Totals;
  onFile: () => void;
}

function PND50Prep({ totals, onFile }: PND50Props) {
  // WHT pre-paid + half-year credits derive from posted journals; until the
  // IS / WHT modules wire those values they remain 0.
  const wht = 0;
  const halfYear = 0;
  const hasNoRows = totals.adds.length === 0 && totals.deds.length === 0 && totals.bookProfit === 0;
  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>แบบ ภ.ง.ด.50 — ภาษีเงินได้นิติบุคคลรายปี</h3>
          <div className="sub">กำหนดยื่น 150 วันหลังสิ้นรอบบัญชี · ระบบเตรียมข้อมูลให้ครบ</div>
        </div>
        <button className="btn primary" onClick={onFile}>
          <Icon name="upload" size={14} /> ยื่นแบบ Online
        </button>
      </div>
      <div className="card-body flush">
        {hasNoRows ? (
          <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
            ยังไม่มีข้อมูลสำหรับช่วงเวลานี้
          </div>
        ) : (
          <table className="tbl">
            <tbody>
              <tr style={{ background: '#fafafa', fontWeight: 700 }}>
                <td colSpan={2}>Schedule A — กำไรสุทธิตามบัญชี</td>
              </tr>
              <tr className="subtotal">
                <td>กำไรสุทธิทางบัญชี</td>
                <td className="num tnum">{fmt(totals.bookProfit)}</td>
              </tr>

              <tr style={{ background: '#fce9e7', color: 'var(--negative)', fontWeight: 700 }}>
                <td colSpan={2}>Schedule B — รายการบวกกลับ (Add-back)</td>
              </tr>
              {totals.adds.length === 0 ? (
                <tr>
                  <td colSpan={2} className="muted" style={{ padding: 16, textAlign: 'center' }}>
                    ยังไม่มีรายการ
                  </td>
                </tr>
              ) : (
                totals.adds.map((a) => {
                  const c = getCat(a.cat);
                  return (
                    <tr key={a.id}>
                      <td className="indent-1">{c.label}</td>
                      <td className="num tnum">+{fmt(a.diff)}</td>
                    </tr>
                  );
                })
              )}
              <tr className="subtotal">
                <td>รวมบวกกลับ</td>
                <td className="num tnum">+{fmt(totals.totalAdd)}</td>
              </tr>

              <tr style={{ background: '#e7f4ec', color: 'var(--positive)', fontWeight: 700 }}>
                <td colSpan={2}>Schedule C — รายการหักออก (Deduct)</td>
              </tr>
              {totals.deds.length === 0 ? (
                <tr>
                  <td colSpan={2} className="muted" style={{ padding: 16, textAlign: 'center' }}>
                    ยังไม่มีรายการ
                  </td>
                </tr>
              ) : (
                totals.deds.map((a) => {
                  const c = getCat(a.cat);
                  return (
                    <tr key={a.id}>
                      <td className="indent-1">{c.label}</td>
                      <td className="num tnum">−{fmt(Math.abs(a.diff))}</td>
                    </tr>
                  );
                })
              )}
              <tr className="subtotal">
                <td>รวมหักออก</td>
                <td className="num tnum">−{fmt(totals.totalDed)}</td>
              </tr>

              <tr style={{ background: '#fafafa' }}>
                <td>กำไรสุทธิทางภาษี</td>
                <td className="num tnum">
                  <b>{fmt(totals.taxProfit)}</b>
                </td>
              </tr>
              <tr>
                <td>× อัตราภาษีนิติบุคคล</td>
                <td className="num">20%</td>
              </tr>
              <tr className="total">
                <td>ภาษีที่ต้องชำระ ภ.ง.ด.50</td>
                <td className="num tnum" style={{ color: 'var(--brand)' }}>
                  {fmt(totals.taxAmount)}
                </td>
              </tr>
              <tr>
                <td>หัก ภาษีหัก ณ ที่จ่ายที่ถูกหักไว้</td>
                <td className="num tnum">({fmt(wht)})</td>
              </tr>
              <tr>
                <td>หัก ภ.ง.ด.51 ที่ชำระแล้ว (ครึ่งปี)</td>
                <td className="num tnum">({fmt(halfYear)})</td>
              </tr>
              <tr className="total">
                <td>ภาษีที่ต้องชำระเพิ่ม</td>
                <td className="num tnum" style={{ color: 'var(--brand)' }}>
                  {fmt(totals.taxAmount - wht - halfYear)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AddManualModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 720,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              fontFamily: 'var(--font-display)',
            }}
          >
            เพิ่มรายการปรับปรุง Manual
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ประเภท
              </label>
              <select
                className="select"
                style={{ width: '100%', height: 38, fontSize: 14 }}
                defaultValue="add"
              >
                <option value="add">บวกกลับ (Add-back)</option>
                <option value="deduct">หักออก (Deduct)</option>
              </select>
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                หมวด
              </label>
              <select
                className="select"
                style={{ width: '100%', height: 38, fontSize: 14 }}
                defaultValue={ADJ_CATS[0]?.code}
              >
                {ADJ_CATS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              รายละเอียด
            </label>
            <textarea
              className="input"
              rows={2}
              style={{ width: '100%', padding: 10, fontSize: 14, minHeight: 60 }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ทางบัญชี
              </label>
              <input
                className="input tnum"
                type="number"
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ทางภาษี
              </label>
              <input
                className="input tnum"
                type="number"
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ผลต่าง (Auto)
              </label>
              <div
                style={{
                  height: 38,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'var(--brand)',
                  color: 'white',
                  borderRadius: 6,
                  fontWeight: 600,
                }}
                className="tnum"
              >
                —
              </div>
            </div>
          </div>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              Auditor / ผู้เสนอ + เหตุผล
            </label>
            <input
              className="input"
              placeholder="เช่น WTW: ค่าใช้จ่ายส่วนตัวของผู้บริหาร"
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              แนบเอกสารประกอบ
            </label>
            <div
              style={{
                border: '2px dashed var(--border-strong)',
                borderRadius: 8,
                padding: '14px',
                textAlign: 'center',
                background: '#fafafa',
                fontSize: 14,
              }}
            >
              ลาก Auditor Letter / Working Paper มาวาง
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={() => {
              /* TODO(phase-3): submit manual adjustment */
            }}
          >
            <Icon name="check" size={14} /> บันทึก & รออนุมัติ
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ item, onClose }: { item: Adjustment; onClose: () => void }) {
  const c = getCat(item.cat);
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 720,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              {item.id} · {c.label}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {c.law}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div
            style={{
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 14,
            }}
          >
            <div>
              <div className="meta">ทางบัญชี</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(item.bookAmt)}
              </div>
            </div>
            <div>
              <div className="meta">ทางภาษี</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(item.taxAmt)}
              </div>
            </div>
            <div>
              <div className="meta">{c.type === 'add' ? 'บวกกลับ' : 'หักออก'}</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  marginTop: 4,
                  color: c.type === 'add' ? 'var(--negative)' : 'var(--positive)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {item.diff > 0 ? '+' : ''}
                {fmt(item.diff)}
              </div>
            </div>
          </div>
          <div>
            <div className="section-title" style={{ margin: '0 0 6px' }}>
              รายละเอียด
            </div>
            <div style={{ fontSize: 14 }}>{item.ref}</div>
          </div>
          {item.auditorNote && (
            <div
              style={{
                padding: '12px 14px',
                background: '#fef3e8',
                borderRadius: 6,
                fontSize: 14,
              }}
            >
              <b>📝 Auditor:</b> {item.auditorNote}
            </div>
          )}
          <div
            style={{
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.55,
            }}
          >
            <b>💡 หลักการ:</b> {c.law}{' '}
            {item.auto
              ? '· ระบบ Auto-calc จาก ' + (c.source ?? '')
              : '· Manual จาก Auditor พร้อม Supporting'}
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {!item.auto && (
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-3): edit manual adjustment */
              }}
            >
              แก้ไข
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FileTaxModal({ totals, onClose }: { totals: Totals; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 680,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              fontFamily: 'var(--font-display)',
            }}
          >
            เตรียมยื่น ภ.ง.ด.50 ปี 2026
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px' }}>
          <div
            style={{
              padding: '16px',
              background: 'var(--brand-tint)',
              borderRadius: 8,
              marginBottom: 14,
              textAlign: 'center',
            }}
          >
            <div className="meta">ภาษีที่ต้องชำระ</div>
            <div
              style={{
                fontSize: 32,
                fontWeight: 700,
                marginTop: 4,
                color: 'var(--brand)',
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(totals.taxAmount)}
            </div>
            <div className="meta">บาท · กำหนดยื่น 31 พ.ค. 2027</div>
          </div>
          <div className="section-title" style={{ margin: '0 0 8px' }}>
            Checklist ก่อนยื่น
          </div>
          {FILE_CHECKLIST.map((t, i) => (
            <label
              key={t}
              style={{
                display: 'flex',
                gap: 8,
                padding: '8px 12px',
                background: '#fafafa',
                borderRadius: 6,
                marginBottom: 6,
                fontSize: 14,
              }}
            >
              <input type="checkbox" defaultChecked={i < 4} /> {t}
            </label>
          ))}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          <button
            className="btn"
            onClick={() => {
              /* TODO(phase-3): export XML */
            }}
          >
            <Icon name="download" size={14} /> Export XML
          </button>
          <button
            className="btn primary"
            onClick={() => {
              /* TODO(phase-3): submit online */
            }}
          >
            <Icon name="upload" size={14} /> ยื่นออนไลน์
          </button>
        </div>
      </div>
    </div>
  );
}
